/**
 * @fileoverview Hook for livestream CRUD operations
 *
 * Provides mutation functions for creating, updating, and deleting livestreams.
 * All operations include proper error handling and loading state management.
 *
 * @module hooks/useLivestreamMutations
 */

import { useCallback, useState } from 'react'
import req from '../api/request'
import type {
  Livestream,
  LivestreamType,
  CreateLivestreamRequest,
  CreateLivestreamResponse,
  UpdateLivestreamRequest,
  LivestreamResponse,
  StreamDetailsResponse,
  UseLivestreamMutationsResult,
  LivestreamApiError,
} from '../types/Livestream'
import {
  LivestreamError,
  LivestreamConflictError,
  LivestreamNotFoundError,
  LivestreamServiceError,
  LivestreamAuthorizationError,
} from '../helpers/LivestreamError'

/**
 * Parses API error response and throws appropriate error class
 * @param err - The caught error
 * @param gameId - Game ID for error context
 * @param type - Livestream type for error context
 * @param operation - Operation name for generic error message
 */
const handleApiError = (
  err: any,
  gameId: string,
  type?: LivestreamType,
  operation: string = 'perform operation'
): never => {
  const statusCode = err?.statusCode || err?.status
  const apiError: LivestreamApiError = err?.data || err

  switch (statusCode) {
    case 403:
      throw new LivestreamAuthorizationError(type || 'unknown')
    case 404:
      throw new LivestreamNotFoundError(type || 'unknown', gameId)
    case 409:
      throw new LivestreamConflictError(type || 'unknown', apiError?.data)
    case 503:
      throw new LivestreamServiceError(apiError?.error)
    default:
      if (err instanceof Error) {
        throw err
      }
      throw new LivestreamError(
        apiError?.error || `Failed to ${operation}`,
        'LIVESTREAM_ERROR',
        statusCode
      )
  }
}

/**
 * Hook for livestream CRUD operations
 *
 * Provides mutation functions for creating, updating, and deleting livestreams.
 * All operations are wrapped in useCallback for stability and include
 * proper error handling with typed error classes.
 *
 * @param gameId - The ID of the game to manage livestreams for
 * @returns Object containing mutation functions, loading state, and error
 *
 * @example
 * ```tsx
 * function ManageLivestream({ gameId }: { gameId: string }) {
 *   const {
 *     createLivestream,
 *     updateLivestream,
 *     deleteLivestream,
 *     getStreamDetails,
 *     loading,
 *     error
 *   } = useLivestreamMutations(gameId)
 *
 *   const handleCreate = async () => {
 *     try {
 *       const result = await createLivestream({
 *         type: 'league',
 *         video_provider_id: 3, // Mux - use VIDEO_PROVIDER_ID.MUX from constants
 *       })
 *       console.log('Stream Key:', result.stream_key)
 *       console.log('RTMP URL:', result.rtmp_url)
 *     } catch (err) {
 *       if (err instanceof LivestreamConflictError) {
 *         console.log('Stream already exists:', err.existingLivestream)
 *       }
 *     }
 *   }
 *
 *   const handleGoLive = async () => {
 *     await updateLivestream('league', { status: 'active' })
 *   }
 *
 *   return (
 *     <div>
 *       <button onClick={handleCreate} disabled={loading}>
 *         Create Stream
 *       </button>
 *       <button onClick={handleGoLive} disabled={loading}>
 *         Go Live
 *       </button>
 *       {error && <ErrorMessage>{error.message}</ErrorMessage>}
 *     </div>
 *   )
 * }
 * ```
 */
export const useLivestreamMutations = (
  gameId: string
): UseLivestreamMutationsResult => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Creates a new livestream for the game
   *
   * @param data - Livestream creation data including type and provider
   * @returns Promise resolving to created livestream with stream credentials
   * @throws {LivestreamConflictError} If livestream of this type already exists
   * @throws {LivestreamServiceError} If Mux is not configured
   * @throws {LivestreamAuthorizationError} If user lacks permission
   */
  const createLivestream = useCallback(
    async (
      data: CreateLivestreamRequest
    ): Promise<CreateLivestreamResponse['data']> => {
      if (!gameId) {
        throw new LivestreamError('Game ID is required', 'INVALID_PARAMS', 400)
      }

      setLoading(true)
      setError(null)

      try {
        const response = (await req(`/games/${gameId}/livestreams`, {
          method: 'POST',
          body: JSON.stringify(data),
        })) as CreateLivestreamResponse

        return response.data
      } catch (err) {
        const thrownError = handleApiError(
          err,
          gameId,
          data.type,
          'create livestream'
        )
        setError(thrownError)
        throw thrownError
      } finally {
        setLoading(false)
      }
    },
    [gameId]
  )

  /**
   * Updates an existing livestream
   *
   * @param type - The livestream type to update
   * @param data - Update data (status, is_upcoming)
   * @returns Promise resolving to updated livestream
   * @throws {LivestreamNotFoundError} If livestream doesn't exist
   * @throws {LivestreamAuthorizationError} If user lacks permission
   */
  const updateLivestream = useCallback(
    async (
      type: LivestreamType,
      data: UpdateLivestreamRequest
    ): Promise<Livestream> => {
      if (!gameId) {
        throw new LivestreamError('Game ID is required', 'INVALID_PARAMS', 400)
      }

      setLoading(true)
      setError(null)

      try {
        const response = (await req(`/games/${gameId}/livestreams/${type}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        })) as LivestreamResponse

        return response.data
      } catch (err) {
        const thrownError = handleApiError(
          err,
          gameId,
          type,
          'update livestream'
        )
        setError(thrownError)
        throw thrownError
      } finally {
        setLoading(false)
      }
    },
    [gameId]
  )

  /**
   * Deletes a livestream
   *
   * Also removes it from Mux. This action cannot be undone.
   *
   * @param type - The livestream type to delete
   * @throws {LivestreamNotFoundError} If livestream doesn't exist
   * @throws {LivestreamAuthorizationError} If user lacks permission
   */
  const deleteLivestream = useCallback(
    async (type: LivestreamType): Promise<void> => {
      if (!gameId) {
        throw new LivestreamError('Game ID is required', 'INVALID_PARAMS', 400)
      }

      setLoading(true)
      setError(null)

      try {
        await req(`/games/${gameId}/livestreams/${type}`, {
          method: 'DELETE',
        })
      } catch (err) {
        const thrownError = handleApiError(
          err,
          gameId,
          type,
          'delete livestream'
        )
        setError(thrownError)
        throw thrownError
      } finally {
        setLoading(false)
      }
    },
    [gameId]
  )

  /**
   * Gets detailed livestream info including RTMP credentials for broadcasting
   *
   * Use this to retrieve the stream_key and rtmp_url for OBS/streaming software.
   *
   * @param type - The livestream type
   * @returns Promise resolving to stream details with credentials
   * @throws {LivestreamNotFoundError} If livestream doesn't exist
   * @throws {LivestreamError} If not a Mux livestream
   */
  const getStreamDetails = useCallback(
    async (type: LivestreamType): Promise<StreamDetailsResponse['data']> => {
      if (!gameId) {
        throw new LivestreamError('Game ID is required', 'INVALID_PARAMS', 400)
      }

      setLoading(true)
      setError(null)

      try {
        const response = (await req(
          `/games/${gameId}/livestreams/${type}/details`
        )) as StreamDetailsResponse

        return response.data
      } catch (err) {
        const thrownError = handleApiError(
          err,
          gameId,
          type,
          'get stream details'
        )
        setError(thrownError)
        throw thrownError
      } finally {
        setLoading(false)
      }
    },
    [gameId]
  )

  return {
    createLivestream,
    updateLivestream,
    deleteLivestream,
    getStreamDetails,
    loading,
    error,
  }
}

export default useLivestreamMutations
